import React, { useState } from "react";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { ChartNode } from "../../../types/api-types";
import { formatDetailValue, getColorForEntityType } from "./link-chart-helpers";
import DetailCard from "./details-panel/detail-card";

interface NodeDetailsPanelProps {
  node: ChartNode;
  onClose: () => void;
}

/**
 * Details panel component that displays information about a selected node
 */
const NodeDetailsPanel: React.FC<NodeDetailsPanelProps> = ({
  node,
  onClose,
}) => {
  const entityColor = getColorForEntityType(node.entityTypeDescription);
  const [hoveredDetail, setHoveredDetail] = useState<string | null>(null);
  const [copiedDetail, setCopiedDetail] = useState<string | null>(null);

  const handleCopy = async (value: unknown, description: string) => {
    const textValue = formatDetailValue(value);
    await navigator.clipboard.writeText(textValue);
    setCopiedDetail(description);
    setTimeout(() => setCopiedDetail(null), 2000);
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Chip
              label={node.entityTypeDescription}
              size="small"
              sx={{
                bgcolor: entityColor,
                color: "white",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {node.label || node.id}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ ml: 1, color: "text.secondary" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Details */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
        }}
      >
        {node.details && node.details.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {node.details.map((detail) => (
              <DetailCard
                key={detail.description}
                detail={detail}
                isHovered={hoveredDetail === detail.description}
                isCopied={copiedDetail === detail.description}
                onMouseEnter={() => setHoveredDetail(detail.description)}
                onMouseLeave={() => setHoveredDetail(null)}
                onCopy={handleCopy}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">No details available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NodeDetailsPanel;
